// src/pages/ChatWithAI/hooks/useThreadHistory.js - ✅ CORRIGIDO: Sem loop infinito
import { useState, useCallback, useRef, useMemo } from 'react';
import { createAxiosWithAuth } from '../utils/chatAuthHelper';

export function useThreadHistory(refreshAccessToken) {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [searchMode, setSearchMode] = useState(false);

  const searchTimeoutRef = useRef(null);
  // Refs em vez de state: mantêm fetchThreads com identidade estável, para que
  // o useEffect que o consome não volte a disparar a cada ciclo de loading.
  const pageRef = useRef(1);
  const loadingRef = useRef(false);
  const initialFetchDone = useRef(false); // ✅ Flag para evitar múltiplos fetches

  const axiosWithAuth = useMemo(() => {
    if (!refreshAccessToken) {
      console.warn('⚠️ useThreadHistory: refreshAccessToken não fornecido');
      return null;
    }
    return createAxiosWithAuth(refreshAccessToken);
  }, [refreshAccessToken]);

  /**
   * ✅ CORRIGIDO: fetchThreads estável (não recria desnecessariamente)
   */
  const fetchThreads = useCallback(async (reset = false) => {
    if (!axiosWithAuth) {
      console.error('❌ Axios não inicializado');
      return;
    }

    // ✅ Prevenir fetch duplicado (ref: não depende do ciclo de render)
    if (loadingRef.current) {
      console.log('⏳ Fetch já em andamento, aguardando...');
      return;
    }

    loadingRef.current = true;

    try {
      setLoading(true);
      setError(null);
      setSearchMode(false);

      const currentPage = reset ? 1 : pageRef.current;

      console.log(`📋 Fetching threads, page: ${currentPage}`);

      const response = await axiosWithAuth.get('/api/chat-threads/list', {
        params: {
          page: currentPage,
          limit: 10
        }
      });

      if (response.data.success) {
        const newThreads = response.data.data;
        
        if (reset) {
          setThreads(newThreads);
          pageRef.current = 1;
        } else {
          setThreads(prev => [...prev, ...newThreads]);
        }

        setHasMore(response.data.pagination.hasMore);

        console.log(`✅ Fetched ${newThreads.length} threads`);
      }

    } catch (err) {
      console.error('❌ Error fetching threads:', err);

      if (err.response?.status === 401 || err.response?.status === 403) {
        setError('Sessão expirada. Por favor, faça login novamente.');
      } else {
        setError(err.response?.data?.message || 'Erro ao carregar histórico');
      }
    } finally {
      // ✅ Marcar como tentado SEMPRE, mesmo em erro: é isto que impede o
      // efeito do sidebar de re-disparar em ciclo quando a resposta é 401.
      initialFetchDone.current = true;
      loadingRef.current = false;
      setLoading(false);
    }
  }, [axiosWithAuth]); // ✅ Identidade estável

  /**
   * ✅ Carregar mais (próxima página)
   */
  const loadMore = useCallback(() => {
    if (!loadingRef.current && hasMore && !searchMode) {
      console.log('📄 Loading next page...');
      // Antes só incrementava o state e nada disparava o fetch. Como fetchThreads
      // deixou de depender de `page`, a paginação passa a ser feita aqui.
      pageRef.current += 1;
      fetchThreads(false);
    }
  }, [hasMore, searchMode, fetchThreads]);

  /**
   * ✅ Pesquisar threads
   */
  const searchThreads = useCallback(async (query) => {
    if (!axiosWithAuth) return;

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!query.trim()) {
      setSearchMode(false);
      setThreads([]);
      initialFetchDone.current = false;
      pageRef.current = 1;
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        loadingRef.current = true;
        setLoading(true);
        setError(null);
        setSearchMode(true);

        console.log(`🔍 Searching threads: "${query}"`);

        const response = await axiosWithAuth.get('/api/chat-threads/search', {
          params: { q: query }
        });

        if (response.data.success) {
          setThreads(response.data.data);
          setHasMore(false);
          
          console.log(`✅ Found ${response.data.data.length} threads`);
        }

      } catch (err) {
        console.error('❌ Error searching threads:', err);
        
        if (err.response?.status === 401 || err.response?.status === 403) {
          setError('Sessão expirada. Por favor, faça login novamente.');
        } else {
          setError(err.response?.data?.message || 'Erro ao pesquisar');
        }
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    }, 300);

  }, [axiosWithAuth]);

  /**
   * ✅ Criar thread
   */
  const createThread = useCallback(async (titulo, casoId = null) => {
    if (!axiosWithAuth) {
      throw new Error('Axios não inicializado');
    }

    try {
      const response = await axiosWithAuth.post('/api/chat-threads/create', {
        titulo,
        id_law_case: casoId
      });

      if (response.data.success) {
        console.log('✅ Thread created:', response.data.data);
        setThreads(prev => [response.data.data, ...prev]);
        return response.data.data;
      }

    } catch (err) {
      console.error('❌ Error creating thread:', err);
      throw err;
    }
  }, [axiosWithAuth]);

  /**
   * ✅ Buscar mensagens
   */
  const getThreadMessages = useCallback(async (threadId) => {
    if (!axiosWithAuth) {
      throw new Error('Axios não inicializado');
    }

    try {
      console.log(`📨 Fetching messages for thread: ${threadId}`);

      const response = await axiosWithAuth.get(`/api/chat-threads/${threadId}/messages`);

      if (response.data.success) {
        console.log(`✅ Loaded ${response.data.data.length} messages`);
        return response.data.data;
      }

      return [];

    } catch (err) {
      console.error('❌ Error fetching thread messages:', err);
      throw err;
    }
  }, [axiosWithAuth]);

  /**
   * ✅ Atualizar título
   */
  const updateThreadTitle = useCallback(async (threadId, novoTitulo) => {
    if (!axiosWithAuth) {
      throw new Error('Axios não inicializado');
    }

    try {
      const response = await axiosWithAuth.put(`/api/chat-threads/${threadId}/title`, {
        titulo: novoTitulo
      });

      if (response.data.success) {
        console.log('✅ Thread title updated');
        setThreads(prev => prev.map(t => 
          t.id === threadId ? { ...t, titulo: novoTitulo } : t
        ));
      }

    } catch (err) {
      console.error('❌ Error updating thread title:', err);
      throw err;
    }
  }, [axiosWithAuth]);

  /**
   * ✅ Limpar pesquisa
   */
  const clearSearch = useCallback(() => {
    setSearchMode(false);
    setThreads([]);
    pageRef.current = 1;
    initialFetchDone.current = false;
  }, []);

  return {
    threads,
    loading,
    error,
    hasMore,
    searchMode,
    fetchThreads,
    loadMore,
    searchThreads,
    createThread,
    getThreadMessages,
    updateThreadTitle,
    clearSearch,
    initialFetchDone // ✅ Expor a REF (antes era .current, um valor congelado no render)
  };
}